import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_FEEDBACK_CHARS,
  canEditFeedback,
  formatBytes,
  renderMentions,
  type FeedbackEntity,
  type FeedbackView,
} from '@goyone/shared';
import { api, qs, upload as uploadFile } from '../lib/api';
import { dateTime, relative } from '../lib/format';
import { useAuth, useSession } from '../auth/AuthProvider';
import { Icon } from './Icon';
import { ErrorState } from './ui';
import { Avatar, useTeamDirectory } from './People';
import { AudioPlayer } from './AttachmentPreview';
import { VoiceRecorder } from './VoiceRecorder';
import { useToast } from './Toast';
import { usePagedNewestFirst } from './Pager';

/**
 * التغذية الراجعة على سجل.
 *
 * مكوّن واحد يُركَّب على العميل والعميل المحتمل وجهة الاتصال والنشاط.
 *
 * وهي ليست الخطّ الزمني: ذاك يسرد ما وقع — مكالمة، تغيّر حالة، مرفق —
 * وهذه رأيٌ فيما وقع، يُكتب ليُقرأ ويُردّ عليه. خلطهما يُغرق سرد الأحداث
 * بحوارٍ لا يخصّ من يقرأ التاريخ.
 *
 * ## رفع الصوت خطوتان
 *
 * التسجيل يُرفع مرفقًا أولًا، ثم تُنشأ الملاحظة مشيرةً إليه. البديل رفعٌ
 * ضمن الملاحظة نفسها يعني مسارًا ثانيًا يقبل `multipart` ويعيد بناء حدّ
 * الحجم وحدّ الباقة وكشف التكرار — وهي مبنيّة في المرفقات.
 *
 * وإن أُنشئت الملاحظة وفشل رفعها، يبقى المرفق يتيمًا في التخزين. مقبول:
 * حدّ الباقة يحصيه، ومن يراجع المرفقات يجده ويحذفه — وهو أهون من ملاحظة
 * تشير إلى صوت لا وجود له.
 */
export function Feedback({
  entity,
  entityId,
  title = 'الملاحظات',
}: {
  entity: FeedbackEntity;
  entityId: string;
  title?: string;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const session = useSession();
  const { atLeast } = useAuth();
  const { byId, assignable } = useTeamDirectory();

  const [body, setBody] = useState('');
  const [audio, setAudio] = useState<File | null>(null);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  const [mentionOpen, setMentionOpen] = useState(false);

  const list = useQuery({
    queryKey: ['feedback', entity, entityId],
    queryFn: () => api<FeedbackView[]>(`/feedback${qs({ entity, entityId })}`),
    enabled: Boolean(entityId),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['feedback', entity, entityId] });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/feedback/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void refresh();
      toast('حُذفت الملاحظة');
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'تعذّر الحذف.'),
  });

  const edit = useMutation({
    mutationFn: (args: { id: string; body: string }) =>
      api(`/feedback/${args.id}`, { method: 'PATCH', body: { body: args.body } }),
    onSuccess: () => {
      setEditing(null);
      void refresh();
      toast('عُدّلت الملاحظة');
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'تعذّر التعديل.'),
  });

  const submit = async () => {
    setError(null);
    if (!body.trim() && !audio) return;

    setBusy(true);
    try {
      let attachmentId: string | undefined;

      if (audio) {
        const form = new FormData();
        form.append('file', audio);
        form.append('entity', 'feedback');
        /*
         * المرفق يُعلَّق على السجل نفسه لا على الملاحظة.
         *
         * الملاحظة لم تُنشأ بعد فلا معرّف لها، ومعرّف السجل قائم — فيبقى
         * المرفق منسوبًا إلى ما يخصّه لو فشلت الملاحظة بعده.
         */
        form.append('entityId', entityId);
        const up = await uploadFile<{ id: string }>('/attachments', form);
        attachmentId = up.id;
      }

      await api('/feedback', {
        method: 'POST',
        body: {
          entity,
          entityId,
          body: body.trim() || undefined,
          attachmentId,
          parentId: replyTo ?? undefined,
        },
      });

      setBody('');
      setAudio(null);
      setReplyTo(null);
      void refresh();
      toast(replyTo ? 'أُضيف الردّ' : 'أُضيفت الملاحظة');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'تعذّر الحفظ.');
    } finally {
      setBusy(false);
    }
  };

  const nameOf = (userId: string) => byId.get(userId)?.name;
  const roots = list.data ?? [];
  const count = roots.reduce((n, r) => n + 1 + r.replies.length, 0);

  /* الأحدث فوق وخمسٌ في الصفحة — كقوائم الملف سواء. */
  const { shown, pager } = usePagedNewestFirst(roots, 5);

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">{title}</h2>
        {count > 0 && <span className="tiny mute">{count}</span>}
      </div>

      <div className="card-body">
        {error && <div className="alert alert-danger mb-4">{error}</div>}

        {/* ---------- المُنشئ ---------- */}
        <div className="fb-composer">
          {replyTo && (
            <div className="tiny mute mb-2">
              ردّ على ملاحظة{' '}
              <button className="link-btn" onClick={() => setReplyTo(null)}>
                إلغاء
              </button>
            </div>
          )}

          <textarea
            className="textarea"
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, MAX_FEEDBACK_CHARS))}
            placeholder={
              entity === 'activity'
                ? 'ما رأيك فيما جرى؟ وما الخطوة التي تقترحها؟'
                : 'ملاحظة يقرأها الفريق — رأيك في الموقف أو ما يجب الانتباه إليه.'
            }
            rows={3}
          />

          <div className="fb-tools">
            <VoiceRecorder onReady={setAudio} onClear={() => setAudio(null)} disabled={busy} />

            {/*
              الذكر يُختار من قائمة ولا يُكتب.
              الاسم المكتوب بيد المستخدم لا يُحلّ إلى مستخدم — «@محمد» في
              فريق فيه ثلاثة محمدين لا يُشعِر أحدًا.
            */}
            <div className="fb-mention">
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => setMentionOpen((v) => !v)}
                aria-expanded={mentionOpen}
              >
                <Icon name="user" size={14} />
                اذكر زميلًا
              </button>

              {mentionOpen && (
                <div className="fb-mention-list">
                  {assignable
                    .filter((m) => m.userId !== session.user.id)
                    .map((m) => (
                      <button
                        key={m.userId}
                        type="button"
                        className="pick-row"
                        onClick={() => {
                          setBody((b) => `${b}${b && !b.endsWith(' ') ? ' ' : ''}@[${m.userId}] `);
                          setMentionOpen(false);
                        }}
                      >
                        <Avatar name={m.name} src={m.avatarUrl} size={24} />
                        <span className="pick-row-text">
                          <strong>{m.name}</strong>
                        </span>
                      </button>
                    ))}
                </div>
              )}
            </div>

            <button
              className="btn btn-primary btn-sm"
              style={{ marginInlineStart: 'auto' }}
              onClick={() => void submit()}
              disabled={busy || (!body.trim() && !audio)}
            >
              <Icon name="send" size={14} />
              {busy ? 'جارٍ الحفظ' : replyTo ? 'ردّ' : 'أضِف'}
            </button>
          </div>

          {body.length > MAX_FEEDBACK_CHARS - 200 && (
            <div className="tiny mute">
              بقي {MAX_FEEDBACK_CHARS - body.length} حرفًا
            </div>
          )}
        </div>

        {/* ---------- القائمة ---------- */}
        {list.isPending && <div className="skeleton mt-4" style={{ height: 100 }} />}
        {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

        {list.data && roots.length === 0 && (
          <p className="mute small center mt-4">
            لا ملاحظات بعد. اكتب أول رأي يقرؤه من يفتح هذا السجل بعدك.
          </p>
        )}

        <div className="fb-list mt-4">
          {shown.map((item, i) => (
            <FeedbackItem
              key={item.id}
              item={item}
              index={i}
              nameOf={nameOf}
              currentUserId={session.user.id}
              canModerate={atLeast('ADMIN')}
              editing={editing}
              editBody={editBody}
              onEditStart={(f) => {
                setEditing(f.id);
                setEditBody(f.body ?? '');
              }}
              onEditChange={setEditBody}
              onEditCancel={() => setEditing(null)}
              onEditSave={(id) => edit.mutate({ id, body: editBody })}
              onReply={setReplyTo}
              onDelete={(id) => remove.mutate(id)}
              pending={edit.isPending || remove.isPending}
            />
          ))}
          {pager}
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   عنصر واحد
   ========================================================================== */

interface ItemProps {
  item: FeedbackView;
  index: number;
  nameOf: (id: string) => string | undefined;
  currentUserId: string;
  canModerate: boolean;
  editing: string | null;
  editBody: string;
  onEditStart: (f: FeedbackView) => void;
  onEditChange: (v: string) => void;
  onEditCancel: () => void;
  onEditSave: (id: string) => void;
  onReply: (id: string) => void;
  onDelete: (id: string) => void;
  pending: boolean;
  isReply?: boolean;
}

function FeedbackItem(props: ItemProps) {
  const {
    item,
    index,
    nameOf,
    currentUserId,
    canModerate,
    editing,
    editBody,
    onEditStart,
    onEditChange,
    onEditCancel,
    onEditSave,
    onReply,
    onDelete,
    pending,
    isReply = false,
  } = props;

  const mine = item.authorId === currentUserId;
  const editable = mine && canEditFeedback(item.createdAt);
  const isEditing = editing === item.id;

  return (
    <div
      className={`fb-item${isReply ? ' fb-reply' : ''} reveal`}
      style={{ ['--reveal-i' as string]: index }}
    >
      <Avatar name={item.authorName ?? 'مستخدم سابق'} size={30} />

      <div className="fb-body">
        <div className="fb-meta">
          <strong>{item.authorName ?? 'مستخدم سابق'}</strong>
          {/*
            التاريخ والساعة مكتوبان لا مخبوءان في تلميح: «الآن» تصير
            كذبًا بعد ساعة، ومن يراجع سجلًّا يريد متى قيل الكلام بالضبط.
          */}
          <span className="tiny mute" title={relative(item.createdAt)}>
            {dateTime(item.createdAt)}
          </span>
          {/* المعدَّل يُعلَّم: التصحيح مقبول وإخفاؤه ليس. */}
          {item.editedAt && <span className="tiny mute">— عُدّلت</span>}
        </div>

        {isEditing ? (
          <div className="mt-2">
            <textarea
              className="textarea"
              value={editBody}
              onChange={(e) => onEditChange(e.target.value)}
              rows={3}
            />
            <div className="row mt-2" style={{ gap: 'var(--sp-2)' }}>
              <button
                className="btn btn-sm btn-primary"
                onClick={() => onEditSave(item.id)}
                disabled={pending || !editBody.trim()}
              >
                حفظ
              </button>
              <button className="btn btn-sm" onClick={onEditCancel}>
                إلغاء
              </button>
            </div>
          </div>
        ) : (
          <>
            {item.body && (
              <p className="fb-text">{renderMentions(item.body, nameOf)}</p>
            )}

            {item.audio && (
              <div className="fb-audio">
                <AudioPlayer id={item.audio.attachmentId} />
                <span className="tiny mute">{formatBytes(item.audio.size)}</span>
              </div>
            )}
          </>
        )}

        {!isEditing && (
          <div className="fb-actions">
            {/* الردّ على الأصل لا على الردّ: مستوى واحد يفرضه الخادم كذلك. */}
            {!isReply && (
              <button className="link-btn tiny" onClick={() => onReply(item.id)}>
                ردّ
              </button>
            )}
            {editable && (
              <button className="link-btn tiny" onClick={() => onEditStart(item)}>
                تعديل
              </button>
            )}
            {(mine || canModerate) && (
              <button
                className="link-btn tiny link-danger"
                onClick={() => onDelete(item.id)}
                disabled={pending}
              >
                حذف
              </button>
            )}
          </div>
        )}

        {item.replies.length > 0 && (
          <div className="fb-replies">
            {item.replies.map((r, i) => (
              <FeedbackItem key={r.id} {...props} item={r} index={i} isReply />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
